/**
 * Invoice Workbench contracts + API helpers (plan 03).
 *
 * Mirrors `app/schemas/ap_workbench.py` and the comparison-data payload built
 * by `app/services/ap/workbench_actions.py`. Row/status types come from
 * `@/lib/ap/invoices` — never fork them here.
 */

import { apiClient } from '@/lib/api-client'
import type { InvoiceStatus, ReviewCategory } from '@/lib/ap/invoices'
import type { SyntheticMeta } from '@/lib/synthetic'
import type { MatchFacts, MatchTraceEvidence } from '@/lib/ap/evidence'
import type { WorkbenchTab } from '@/lib/ap/auditDisplay'
import { displayLocale } from '@/lib/display-locale'

export type { EvidenceComparison, EvidenceSearch, MatchFacts, MatchTraceEvidence } from '@/lib/ap/evidence'

export type ActionName =
  | 'approve'
  | 'reject'
  | 'hold'
  | 'queue_qa'
  | 'return_to_review'
  | 'send_to_vendor'
  | 'reassign'
  | 'comment'
  | 'rematch'
  | 'edit'
  | 'push_to_sap'
  /** Plan 11: withdraw the open approval request (`POST /invoices/{id}/actions/cancel-approval`). */
  | 'withdraw_approval'

/** Plan 11: who the invoice waits on while an approval request is open (`InvoiceDetail.approval_notice`). */
export interface ApprovalNotice {
  approver_name: string | null
  approver_title: string | null
  policy_code: string | null
  policy_name: string | null
  reason_short: string | null
  since: string | null
  routed_by?: string | null
}

export type TraceStatus = 'PASS' | 'FAIL' | 'WARNING' | 'INFO'

export interface TraceStep {
  step: string
  status: TraceStatus | string
  message?: string | null
  details?: Record<string, unknown> | null
  /** Structured evidence (plan 03 §4.2); absent on traces recorded before it existed. */
  evidence?: MatchTraceEvidence | null
}

export interface TracePolicyRef {
  policy_id: string | null
  policy_name: string | null
  rule_code: string | null
  policy_scope: string | null
  kind: 'policy' | 'automation_rule' | 'setting'
  href: string
}

export interface InvoiceDetail {
  id: number
  invoice_id: string
  vendor_name: string | null
  vendor_address: string | null
  buyer_name: string | null
  buyer_address: string | null
  shipping_address: string | null
  billing_address: string | null
  payment_terms: string | null
  other_header_fields: Record<string, unknown> | null
  invoice_metadata: Record<string, unknown> | null
  related_po_numbers: string[] | null
  related_grn_numbers: string[] | null
  invoice_date: string | null
  due_date: string | null
  subtotal: number | null
  tax: number | null
  grand_total: number | null
  currency: string | null
  line_items: Record<string, unknown>[] | null
  match_trace: TraceStep[] | null
  match_confidence: number | null
  ai_recommendation: unknown
  status: InvoiceStatus
  review_category: ReviewCategory | string | null
  notes: string | null
  gl_code: string | null
  assigned_to: string | null
  hold_until: string | null
  hold_reason: string | null
  sla_breached: boolean
  is_duplicate: boolean
  duplicate_of_id: number | null
  payment_batch_id: string | null
  paid_date: string | null
  source_filename: string | null
  has_file: boolean
  version: number
  created_at: string
  updated_at: string
  allowed_actions: ActionName[]
  /** Latest SAP posting (present once the invoice has been posted; also fetched via `getSapPosting`). */
  sap_posting?: SapPosting | null
  /** Why SAP posting is blocked by a `sap_post` AI Policy (e.g. AP-SAP-SYN-001); null when it is not. */
  sap_post_block?: { reason: string; rule_code: string | null; policy_name: string | null } | null
  /** Source language (plan 03 WP-WB-01); the label is resolved server-side from data. */
  source_language_code?: string | null
  source_language_name?: string | null
  source_language_label?: string | null
  /** Open approval request (plan 11); null when the invoice is not waiting on an approver. */
  approval_notice?: ApprovalNotice | null
  /** `{ header: { vendor_name, payment_terms, invoice_date_printed, ... } }` as printed. */
  original_language_fields?: Record<string, unknown> | null
}

// ─── SAP posting (mirrors the SAP contract: sap-preview / push-to-sap / sap-posting) ───────

export type SapStepKey = 'validate_format' | 'check_credentials' | 'check_connection' | 'post_document' | 'confirm'

export interface SapPostingStep {
  key: SapStepKey | string
  label: string
  status: 'done' | 'failed' | 'skipped'
  started_at: string | null
  finished_at: string | null
  detail: string | null
}

export interface SapMessage {
  severity: 'success' | 'info' | 'error'
  text: string
}

export interface SapPosting {
  id: number
  invoice_db_id: number
  invoice_id: string
  status: 'posted' | 'failed'
  sap_document_number: string | null
  company_code: string | null
  fiscal_year: string | null
  system_id: string | null
  client: string | null
  document_type: string | null
  payload: SapJournalEntryPayload | Record<string, unknown> | null
  response_xml: string | null
  messages: SapMessage[]
  steps: SapPostingStep[]
  posted_by: string | null
  posted_at: string | null
}

export interface SapTarget {
  system_id: string
  client: string
  company_code: string
  company_name: string
  document_type: string
  gl_account: string
  tax_code: string
}

export interface SapJournalEntryItem {
  GLAccount: string
  CurrencyCode: string
  AmountInTransactionCurrency: number | string
  Tax?: { TaxCode: string }
  DocumentItemText?: string
  PurchaseOrder?: string | null
  PurchaseOrderItem?: string | number | null
}

/** JournalEntryBulkCreateRequest-shaped JSON built from the invoice. Only the fields the UI reads are typed. */
export interface SapJournalEntryPayload {
  MessageHeader?: { CreationDateTime?: string }
  JournalEntryCreateRequest?: {
    JournalEntry?: {
      OriginalReferenceDocumentType?: string
      BusinessTransactionType?: string
      AccountingDocumentType?: string
      DocumentReferenceID?: string
      CompanyCode?: string
      DocumentDate?: string
      PostingDate?: string
      CreatedByUser?: string
      Item?: SapJournalEntryItem[]
      CreditorItem?: { Creditor?: string; AmountInTransactionCurrency?: number | string; DueCalculationBaseDate?: string }
    }
  }
}

/** Outcome of the `sap_post` AI Policies (decision A6): e.g. AP-SAP-SYN-001 blocks synthetic-backed invoices. */
export interface SapPostingGuard {
  blocked: boolean
  reason: string | null
  rule_code: string | null
  policy_name: string | null
}

export interface SapPreview {
  target: SapTarget
  payload: SapJournalEntryPayload
  guard?: SapPostingGuard
}

export interface PushToSapResult {
  invoice: InvoiceDetail
  posting: SapPosting
}

/** The five posting steps, in order — the dialog renders these as pending until the server's steps arrive. */
export const SAP_POSTING_STEPS: { key: SapStepKey; label: (target?: SapTarget | null) => string }[] = [
  { key: 'validate_format', label: () => 'Validating SAP document format' },
  { key: 'check_credentials', label: () => 'Verifying SAP credentials' },
  { key: 'check_connection', label: (t) => (t ? `Connecting to ${t.system_id} / ${t.client}` : 'Connecting to SAP') },
  { key: 'post_document', label: () => 'Posting journal entry' },
  { key: 'confirm', label: () => 'Confirming SAP document' },
]

export interface NormalizedLine {
  description: string
  sku: string | null
  quantity: number | null
  unit: string | null
  unit_price: number | null
  total: number | null
  /** Original-script text when the invoice was not in English (WP-WB-01). */
  description_original?: string | null
  unit_original?: string | null
  raw: Record<string, unknown>
  po_number?: string
  grn_number?: string
  line_key?: string
}

export interface LineComparison {
  line_key: string
  invoice_line: NormalizedLine
  po_line: NormalizedLine | null
  grn_line: NormalizedLine | null
  po_number: string | null
  grn_number: string | null
}

export interface RelatedPo {
  id: number
  po_number: string
  vendor_name: string | null
  buyer_name: string | null
  order_date: string | null
  line_items: NormalizedLine[]
  po_grand_total: number | null
  grand_total: number | null
  status: string
  has_file: boolean
  /** Synthetic (demo) provenance (plan docs/revamp-new/02 §3.2). */
  is_synthetic?: boolean
  synthetic_meta?: SyntheticMeta | null
  sap?: Record<string, unknown> | null
}

export interface RelatedGrn {
  id: number
  grn_number: string
  po_number: string | null
  received_date: string | null
  line_items: NormalizedLine[]
  status: string
  has_file: boolean
  is_synthetic?: boolean
  synthetic_meta?: SyntheticMeta | null
  sap?: Record<string, unknown> | null
}

export interface DocumentRef {
  id: number
  has_file: boolean
  file_url: string | null
  po_number?: string
  grn_number?: string
}

export interface Suggestion {
  heuristic_id: number
  exception_type: string
  message: string
  action: string
  confidence: number
}

export interface RelatedContract {
  id: number
  contract_number: string
  title: string
  result: 'pass' | 'fail' | 'warning' | null
  rate_page: number | null
  has_file: boolean
  file_url: string | null
}

export interface ComparisonData {
  invoice_id: string
  invoice_db_id: number
  version: number
  invoice_header_data: Record<string, unknown>
  vendor_name: string | null
  grand_total: number | null
  line_item_comparisons: LineComparison[]
  related_pos: RelatedPo[]
  related_grns: RelatedGrn[]
  missing_po_numbers: string[]
  missing_grn_numbers: string[]
  invoice_notes: string | null
  invoice_status: InvoiceStatus
  review_category: string | null
  match_trace: TraceStep[]
  match_facts: MatchFacts | null
  match_confidence: number | null
  /** "4-way" when the contract rate card was checked (docs/ultratech/08-four-way-match.md). */
  match_kind?: '3-way' | '4-way'
  related_contracts?: RelatedContract[]
  trace_policy_index: Record<string, TracePolicyRef>
  /** Who evaluated the trace, in the audit trail's words (decision A1), and when. */
  match_worker?: { label: string | null; matched_at: string | null } | null
  gl_code: string | null
  related_documents: { invoice: DocumentRef; pos: DocumentRef[]; grns: DocumentRef[] }
  suggestion: Suggestion | null
  allowed_actions: ActionName[]
  /** Setting `ap.synthetic_documents.enabled` (Workbench fallback button). */
  synthetic_documents_enabled?: boolean
  has_synthetic_documents?: boolean
  /** Verdict of the eligibility policy AP-SYN-ELIG-001 (unknown or missing PO only). */
  synthetic_eligible?: boolean
  synthetic_link_state?: string | null
}

export interface Siblings {
  ids: number[]
  index: number
  total: number
  queue_label: string
  prev_id: number | null
  next_id: number | null
}

export type TimelinePhase =
  | 'ingestion'
  | 'extraction'
  | 'linking'
  | 'matching'
  | 'policy'
  | 'human'
  | 'payment'
  | 'synthetic'
  | 'approval'

export interface TimelineLink {
  tab: WorkbenchTab | string
  label: string
  /** The part of the headline rendered as the link. */
  text?: string | null
}

export interface TimelineEntry {
  id: number
  timestamp: string
  user: string
  action: string
  summary: string
  details: Record<string, unknown> | null
  phase: TimelinePhase
  /** Display fields from app/config/audit_event_display.json (WP-WB-07); absent on an older backend. */
  actor_display?: string | null
  actor_kind?: 'ai_worker' | 'human' | 'system' | null
  headline?: string | null
  link?: TimelineLink | null
}

export interface InvoiceComment {
  id: number
  invoice_id: number
  user: string
  text: string
  type: string
  created_at: string
}

export interface ActionResult {
  invoice_id: number
  invoice_id_str: string
  from_status: InvoiceStatus
  to_status: InvoiceStatus
  audit_id: number | null
  message: string
  allowed_actions: ActionName[]
  version: number
}

export interface EmailDraft {
  to_email: string | null
  subject: string
  body: string
  generated_by: 'ai' | 'template'
}

export interface TeachResponse {
  draft_policy: Record<string, unknown>
  analysis: {
    conflicts: unknown[]
    overrides: unknown[]
    clarifications: string[]
    suggested_instructions: string[]
    refined_instruction?: string | null
    warnings: string[]
    is_valid: boolean
  }
  policy_id: string | null
}

/** Header keys the backend allow-lists (S13). Anything else is rejected with 422. */
export const HEADER_EDITABLE_FIELDS = [
  'invoice_id',
  'vendor_name',
  'invoice_date',
  'due_date',
  'subtotal',
  'tax',
  'grand_total',
  'currency',
  'payment_terms',
  'vendor_address',
  'buyer_name',
  'buyer_address',
  'billing_address',
  'shipping_address',
  'notes',
] as const

export type HeaderField = (typeof HEADER_EDITABLE_FIELDS)[number]
export type HeaderDraft = Partial<Record<HeaderField, string | number | null>>

// ─── API ────────────────────────────────────────────────────────────────────

const base = (id: number) => `/api/invoices/${id}`

export const workbenchApi = {
  detail: (id: number) => apiClient.get<InvoiceDetail>(base(id)),
  comparison: (id: number) => apiClient.get<ComparisonData>(`${base(id)}/comparison-data`),
  siblings: (id: number, queue: string | null) =>
    apiClient.get<Siblings>(`${base(id)}/siblings${queue ? `?queue=${encodeURIComponent(queue)}` : ''}`),
  auditLog: (id: number) => apiClient.get<TimelineEntry[]>(`${base(id)}/audit-log`),
  comments: (id: number) => apiClient.get<InvoiceComment[]>(`${base(id)}/comments`),
  addComment: (id: number, text: string) => apiClient.post<InvoiceComment>(`${base(id)}/comments`, { text }),
  approve: (id: number, body: { version: number; reason?: string; two_way_override?: boolean }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/approve`, body),
  reject: (id: number, body: { version: number; reason: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/reject`, body),
  hold: (id: number, body: { version: number; hold_days: number; reason: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/hold`, body),
  queue: (id: number, body: { version: number; target: 'qa_approval' | 'needs_review'; reason?: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/queue`, body),
  sendToVendor: (id: number, body: { version: number; to_email?: string; subject?: string; message: string }) =>
    apiClient.post<ActionResult>(`${base(id)}/actions/send-to-vendor`, body),
  rematch: (id: number, version: number) => apiClient.post<ActionResult>(`${base(id)}/actions/rematch`, { version }),
  updateHeader: (id: number, body: HeaderDraft & { version: number }) =>
    apiClient.put<InvoiceDetail>(`${base(id)}/header`, body),
  updateLinks: (id: number, body: { version: number; related_po_numbers?: string[]; related_grn_numbers?: string[] }) =>
    apiClient.put<ActionResult>(`${base(id)}/links`, body),
  emailDraft: (id: number, kind: 'price' | 'quantity' | 'generic') =>
    apiClient.post<EmailDraft>(`${base(id)}/email-draft`, { kind }),
  teach: (id: number, body: { instruction: string; trace_step?: string; confirm: boolean }) =>
    apiClient.post<TeachResponse>(`${base(id)}/teach`, body),
  sapPosting: (id: number) => getSapPosting(id),
}

/** GET /api/invoices/{id}/sap-preview — target + JournalEntryBulkCreateRequest payload built from the invoice. */
export function getSapPreview(id: number): Promise<SapPreview> {
  return apiClient.get<SapPreview>(`${base(id)}/sap-preview`)
}

/** POST /api/invoices/{id}/actions/push-to-sap — performs the posting; the invoice lands in `posted`. */
export function pushToSap(id: number, comment?: string | null, version?: number): Promise<PushToSapResult> {
  return apiClient.post<PushToSapResult>(`${base(id)}/actions/push-to-sap`, { comment: comment ?? null, ...(version != null ? { version } : {}) })
}

export interface SyntheticDocumentsResult {
  invoice_db_id: number
  invoice_status: string
  replayed: boolean
  purchase_order: { id: number; po_number: string | null }
  goods_receipt_note: { id: number; grn_number: string | null }
  references_added: { po_numbers: string[]; grn_numbers: string[] }
}

/** POST /api/invoices/{id}/synthetic-documents — Workbench fallback: mirror PO + GRN, then re-match (WP-AUTO-04). */
export function generateSyntheticDocuments(id: number, version: number): Promise<SyntheticDocumentsResult> {
  return apiClient.post<SyntheticDocumentsResult>(`${base(id)}/synthetic-documents`, { version })
}

/** GET /api/invoices/{id}/sap-posting — latest posting, or 404 (rejected promise) when none exists. */
export function getSapPosting(id: number): Promise<SapPosting> {
  return apiClient.get<SapPosting>(`${base(id)}/sap-posting`)
}

/** The one PDF endpoint (R4 / plan 08 E5). Fetched raw with a bearer token, never via apiClient. */
export function documentFileUrl(kind: 'invoice' | 'purchase_order' | 'goods_receipt_note', id: number): string {
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
  return `${apiUrl}${basePath}/api/ap/documents/${kind}/${id}/file`
}

/** Workbench URL that keeps the originating queue (R14) and, when given, the open tab. */
export function workbenchUrl(invoiceId: number, queue: string | null, tab?: WorkbenchTab | null): string {
  const q = queue ? `&queue=${encodeURIComponent(queue)}` : ''
  const t = tab && tab !== 'review' ? `&tab=${tab}` : ''
  return `/workbench?invoice=${invoiceId}${q}${t}`
}

// ─── Trace helpers (ported semantics from legacy MatchTrace / LineItemComparisonTable) ─────

/** Strip embedded JSON blobs and map raw literals to prose (legacy MatchTrace.tsx:5-27). */
export function cleanTraceMessage(message: string | null | undefined): string {
  if (!message) return ''
  let out = message
  out = out.replace(/\{[^{}]*\}/g, (m) => (m.length > 40 ? '' : m))
  out = out.replace(/\bnull\b/gi, 'not available').replace(/\btrue\b/g, 'yes').replace(/\bfalse\b/g, 'no')
  out = out.replace(/\s{2,}/g, ' ').trim()
  return out
}

export type { LineClass } from '@/lib/line-status'
export { classifyLine, lineSteps } from '@/lib/line-status'

/** Header-level FAIL steps that need a human (legacy ExceptionSummary.tsx:45-62). */
export function resolutionSteps(trace: TraceStep[]): TraceStep[] {
  return trace.filter((s) => {
    if (s.status !== 'FAIL') return false
    if (s.details && typeof s.details.line_key === 'string') return false
    const n = s.step.toLowerCase()
    if (n.startsWith('item ')) return false
    if (n.includes('final result')) return false
    if ((n.includes('price') || n.includes('quantity')) && n.includes('match')) return false
    return true
  })
}

export function summaryCounts(trace: TraceStep[]) {
  return trace.reduce(
    (acc, s) => {
      if (s.status === 'PASS') acc.passed += 1
      else if (s.status === 'FAIL') acc.failed += 1
      else if (s.status === 'WARNING') acc.warnings += 1
      else acc.info += 1
      return acc
    },
    { passed: 0, failed: 0, warnings: 0, info: 0 }
  )
}

/** Money in the selected country's locale (en-IN lakh grouping, es-CO, ...); ALL uses the default. */
export function formatMoney(
  value: number | null | undefined,
  currency?: string | null,
  locale: string = displayLocale(),
): string {
  if (value == null || Number.isNaN(value)) return '—'
  const plain = value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  // A missing currency is shown as a bare number, never as a guessed USD amount.
  if (!currency) return plain
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(value)
  } catch {
    return `${currency} ${plain}`
  }
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

/** Audit phase badge tones: brand and status tokens only (Rule 03); the phase name is always shown as text. */
export const PHASE_COLORS: Record<TimelineEntry['phase'], string> = {
  ingestion: 'bg-brand-cornflower/15 text-brand-navy',
  extraction: 'bg-brand-cornflower/15 text-brand-navy',
  linking: 'bg-brand-cornflower/15 text-brand-navy',
  matching: 'bg-brand-purple/10 text-brand-purple',
  policy: 'bg-brand-purple/10 text-brand-purple',
  human: 'bg-warning-wash text-warning-strong',
  payment: 'bg-success-strong/10 text-success-strong',
  synthetic: 'bg-brand-purple/10 text-brand-purple',
  approval: 'bg-success-strong/10 text-success-strong',
}
